import { GridCellKind } from '@glideapps/glide-data-grid';
import type { CustomCell, CustomRenderer } from '@glideapps/glide-data-grid';
import {
  STAGE_LABELS,
  onlyObserved,
  stageChoicesOf,
  type FundStageView,
  type StageChoice,
} from '@nemo/core/model';
import { type Chip, paintChips } from './chipPainting.js';
import { StagePicker } from './StagePicker.js';

export interface StagesData {
  readonly kind: 'stages';
  /** The merged answer, which is what the chips show. */
  readonly stages: readonly FundStageView[];
  /** The picker's draft: every stage, two boxes each. */
  readonly choices: readonly StageChoice[];
}

export type StagesCell = CustomCell<StagesData>;

/**
 * One chip per stage the fund invests at, in stage order, with a dot where it
 * leads there. A chip only the rounds vouch for is hollow, as every observed
 * value in the grid is, and one with a "!" carries a person's answer newer
 * evidence disagrees with.
 */
export function stageChips(stages: readonly FundStageView[]): Chip[] {
  return shownStages(stages).map((entry) => ({
    label: STAGE_LABELS[entry.stage],
    tone: 'neutral' as const,
    style: onlyObserved(entry) || !entry.participates ? ('hollow' as const) : undefined,
    mark: entry.leads ? ('lead' as const) : undefined,
    flag: entry.conflicts.length > 0 ? ('conflict' as const) : undefined,
  }));
}

/**
 * The stages a chip is shown for: the ones it invests at, and any a person
 * said it does not that newer evidence disagrees with, so the question is
 * where the stage would be.
 */
export function shownStages(stages: readonly FundStageView[]): FundStageView[] {
  return stages.filter((entry) => entry.participates || entry.conflicts.length > 0);
}

export const stagesRenderer: CustomRenderer<StagesCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is StagesCell =>
    'kind' in cell.data && cell.data.kind === 'stages',
  draw: (args, cell) => {
    paintChips(args, stageChips(cell.data.stages));
  },
  provideEditor: () => ({
    disablePadding: true,
    editor: (props) => (
      <StagePicker
        choices={props.value.data.choices}
        onChange={(choices) =>
          props.onChange({ ...props.value, data: { ...props.value.data, choices } })
        }
        onCommit={() => props.onFinishedEditing(props.value)}
        onRevert={() => props.onFinishedEditing(undefined)}
      />
    ),
  }),
};

export function stagesCell(stages: readonly FundStageView[], copyData: string): StagesCell {
  return {
    kind: GridCellKind.Custom,
    allowOverlay: true,
    copyData,
    data: { kind: 'stages', stages, choices: stageChoicesOf({ stages }) },
  };
}
